import { invitedByAtom, isLoggedInAtom, userInfoAtom } from 'app/atoms';
import { useRoute } from 'app/hooks/navigation';
import { useOnboarding } from 'app/hooks/use-onboarding';
import { Event, track } from 'app/lib/analytics';
import { useCreateIdentity } from 'app/network/identity';
import { useCreateOnePlan } from 'app/network/plan';
import { useMyProfile } from 'app/network/profile';
import { useCreateUser } from 'app/network/user';
import type { CredentialsFormSchema } from 'app/screens/onboarding/hooks/use-credentials-form';
import type { User } from 'app/types/user';
import { useAtomValue, useSetAtom } from 'jotai/index';
import { useEffect, useRef, useState } from 'react';
import type { UseFormGetValues, UseFormHandleSubmit } from 'react-hook-form';

export function useCredentialsSignupFlow({
  getValues,
  handleSubmit,
  requireActivationCode,
}: {
  getValues: UseFormGetValues<CredentialsFormSchema>;
  handleSubmit: UseFormHandleSubmit<CredentialsFormSchema>;
  requireActivationCode: boolean;
}) {
  const { goToNext } = useOnboarding();
  const route = useRoute<'credentials'>();
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const userInfo = useAtomValue(userInfoAtom);
  const setInvitedBy = useSetAtom(invitedByAtom);
  const setUserInfo = useSetAtom(userInfoAtom);
  const createdUserRef = useRef<User | undefined>(undefined);

  const [goToNextWhenUserInfo, setGoToNextWhenUserInfo] = useState(false);

  useMyProfile();

  const { mutate: createOnePlan, error: createOnePlanError } = useCreateOnePlan({
    onSuccess: () => {
      goToNext();
    },
  });

  const { mutate: identityMutate, isPending: identityLoading } = useCreateIdentity({
    onSuccess: () => {
      if (createdUserRef.current) setUserInfo(createdUserRef.current);
      setGoToNextWhenUserInfo(true);
    },
  });

  useEffect(() => {
    if (goToNextWhenUserInfo && userInfo) {
      goToNext();
    }
  }, [goToNext, goToNextWhenUserInfo, userInfo]);

  const { error, mutate, isPending } = useCreateUser({
    onSuccess: (user, variables) => {
      track(Event.SignUpSuccess, { provider: 'Email' });
      // Authentication clears the query cache. Retain the creation response until
      // login succeeds, then publish it so onboarding can advance without the BFF.
      createdUserRef.current = user;
      identityMutate(variables);
    },
  });

  const onSubmit = handleSubmit((form) => {
    setInvitedBy(getValues('inviteCode'));
    mutate({ ...form });
  });

  const onSubmitInviteCode = () => {
    const inviteCode = getValues('inviteCode');
    if (requireActivationCode) {
      createOnePlan({ code: inviteCode ?? '' });
      return;
    }
    setInvitedBy(inviteCode);
    goToNext();
  };

  const isLoading = isPending || identityLoading || goToNextWhenUserInfo;

  return {
    createOnePlanError,
    error,
    inviteCodeFirst: !!route.params?.inviteCodeFirst,
    isLoading,
    isLoggedIn,
    onSubmit,
    onSubmitInviteCode,
  };
}
